import { inject, Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { of, shareReplay, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { fetchIfNotLoaded } from '@core/ngrx-legacy';
import {
  HiringResourcesActions,
  HiringResourcesSelectors,
} from '@core/tellent-assign-members-to-jobs-data';
import { HiringResourceScope } from '@core/tellent-assign-members-to-jobs-types';
import { TellentAppsDataService } from '@core/tellent-data';
import { SeatKind } from '@core/tellent-types';

@Injectable()
export class HiringResourcesDataService {
  private readonly store = inject(Store);
  private readonly tellentApps = inject(TellentAppsDataService);

  private readonly recruiteeGuid$ = this.tellentApps
    .getAppBySeatKind(SeatKind.Recruitment)
    .pipe(map(app => app.guid));

  public readonly jobs$ = this.recruiteeGuid$.pipe(
    switchMap(appGuid => {
      if (!appGuid) return of(null);
      return this.store.pipe(
        this.fetchIfNotLoaded(appGuid, 'jobs'),
        select(HiringResourcesSelectors.jobs),
      );
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  public readonly talentPools$ = this.recruiteeGuid$.pipe(
    switchMap(appGuid => {
      if (!appGuid) return of(null);
      return this.store.pipe(
        this.fetchIfNotLoaded(appGuid, 'talentPools'),
        select(HiringResourcesSelectors.talentPools),
      );
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  public readonly jobsLoaded$ = this.store
    .select(HiringResourcesSelectors.isLoaded({ scope: 'jobs' }))
    .pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );

  public readonly talentPoolsLoaded$ = this.store
    .select(HiringResourcesSelectors.isLoaded({ scope: 'talentPools' }))
    .pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );

  private fetchIfNotLoaded(appGuid: string, scope: HiringResourceScope) {
    return fetchIfNotLoaded(this.store.select(HiringResourcesSelectors.meta({ scope })), () =>
      this.store.dispatch(HiringResourcesActions.fetchCollection({ payload: { appGuid, scope } })),
    );
  }
}
